/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Row, Col, size } from '@everlywell/leaves';
import { GatsbyImage as Img } from 'gatsby-plugin-image';
import { throttle } from 'lodash-es';
import React, { useState, useEffect, useRef } from 'react';
import { animated as A, useSpring, config } from 'react-spring';
import { useScrolledIntoViewOnce, useScrolledIntoView } from 'utils/hooks';
import { ContentWithImage, ImageType } from 'utils/types';

import * as S from './styles';

export interface InsightsSectionProps {
  title: string;
  headline: string;
  listOfContent: ContentWithImage[];
  mobileImages: ImageType[];
  desktopImages: ImageType[];
}

// funtion for parallax effect in mobile
const calc = (o: any) => `translateY(${o * 0.075}px)`;

export const InsightsSection: React.FC<InsightsSectionProps> = ({
  title,
  headline,
  listOfContent,
  mobileImages,
  desktopImages,
}) => {
  // state for triggering animation springs
  const [titleInView, setTitleInView] = useState<boolean>(false);
  const [headlineInView, setHeadlineInView] = useState<boolean>(false);
  const [cardsInView, setCardsInView] = useState<boolean>(false);
  const [mobileImageInView, setMobileImageInView] = useState<boolean>(false);
  const [mobileImageOffset, setMobileImageOffset] = useState<number>(0);

  // Animation Refs
  const sectionRef = useRef<HTMLDivElement>(null);
  const { inView: sectionInView } = useScrolledIntoView(sectionRef);

  const titleRef = useScrolledIntoViewOnce(() => {
    setTitleInView(true);
  });
  const headlineRef = useScrolledIntoViewOnce(() => {
    setHeadlineInView(true);
  });

  const mobileImageRef = useScrolledIntoViewOnce(() => {
    setMobileImageInView(true);
  });

  const cardsRef = useScrolledIntoViewOnce(() => {
    setCardsInView(true);
  });

  const [{ offset }, set] = useSpring(() => ({ offset: 0 }));

  // attach the parallax scroll event only when this section is in view
  useEffect(() => {
    // create parallax effect for desktop images onScroll
    const handleScroll: () => void = () => {
      if (!sectionRef || !sectionRef.current) {
        return;
      }
      const { bottom: sectionBottom } =
        sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const offset = windowHeight - sectionBottom;
      set({ offset });
    };
    const handleScrollThrottled = throttle(handleScroll, 25);
    if (sectionRef && sectionInView) {
      window.addEventListener('scroll', handleScrollThrottled);
    } else {
      window.removeEventListener('scroll', handleScrollThrottled);
    }
    return () => {
      window.removeEventListener('scroll', handleScrollThrottled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionRef, sectionInView]);

  // get the mobileImage's height and set for use in mobileImageTransition spring
  useEffect(() => {
    if (mobileImageRef && mobileImageRef.current) {
      setMobileImageOffset(mobileImageRef.current.clientHeight);
    }
  }, [mobileImageRef]);

  // triggered from scrolling into view
  const titleTransition = useSpring({
    opacity: titleInView ? 1 : 0,
    transform: titleInView ? 'translateY(0)' : `translateY(${size.xl1}px)`,
    config: config.slow,
  });

  // triggered from scrolling into view
  const headlineTransition = useSpring({
    opacity: headlineInView ? 1 : 0,
    transform: headlineInView ? 'translateY(0)' : `translateY(${size.xl2}px)`,
    config: config.slow,
  });

  // triggered from scrolling into view
  const cardsTransition = useSpring({
    opacity: cardsInView ? 1 : 0,
    transform: cardsInView ? 'translateY(0)' : `translateY(${size.xl3}px)`,
    config: config.slow,
  });

  // triggered from scrolling into view
  const mobileImageTransition = useSpring({
    transform: mobileImageInView
      ? 'translateY(0)'
      : `translateY(${mobileImageOffset}px)`,
    config: config.slow,
  });

  return (
    <div ref={sectionRef}>
      <S.Container>
        <Row center="xs">
          <Col xs={12} md={8} lg={6}>
            <div ref={titleRef}>
              <A.div style={titleTransition}>
                <S.Title text={title} />
              </A.div>
            </div>
          </Col>
        </Row>
        <Row center="xs">
          <Col xs={12} md={8} lg={6}>
            <div ref={headlineRef}>
              <A.div style={headlineTransition}>
                <S.Headline text={headline} />
              </A.div>
            </div>
          </Col>
        </Row>
        <Row center="xs" reverse>
          <Col xs={12} md={6}>
            <div ref={cardsRef}>
              <S.InfoBlockContainer>
                {listOfContent.length &&
                  listOfContent.map((content, index) => (
                    <A.div style={cardsTransition} key={index}>
                      <S.InfoBlock
                        title={content.heading}
                        image={
                          <Img
                            image={content.image.gatsbyImageData}
                            alt={content.image.description || ''}
                            style={{ width: '100%', height: '100%' }}
                          />
                        }
                        imageAlt={content.image.description || ''}
                        content={content.description?.description || ''}
                        cta={content.cta}
                        size="small"
                      />
                    </A.div>
                  ))}
              </S.InfoBlockContainer>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <S.MainImageContainer ref={mobileImageRef}>
              <S.MainImageContent>
                <S.MobileAnimatedImage style={mobileImageTransition}>
                  <Img
                    image={mobileImages[0].gatsbyImageData}
                    alt={mobileImages[0].description || ''}
                  />
                </S.MobileAnimatedImage>
              </S.MainImageContent>
            </S.MainImageContainer>
            <S.DesktopImageContainer>
              <S.MiddleImage aria-hidden="true">
                <Img
                  image={desktopImages[1]?.gatsbyImageData}
                  alt={desktopImages[1]?.description || ''}
                />
              </S.MiddleImage>
              <S.TopImage
                aria-hidden="true"
                style={{ transform: offset.to(calc) }}
              >
                <Img
                  image={desktopImages[0].gatsbyImageData}
                  alt={desktopImages[0].description || ''}
                />
              </S.TopImage>
              <S.BottomImage
                aria-hidden="true"
                style={{ transform: offset.to(calc) }}
              >
                <Img
                  image={desktopImages[2].gatsbyImageData}
                  alt={desktopImages[2].description || ''}
                />
              </S.BottomImage>
            </S.DesktopImageContainer>
          </Col>
        </Row>
      </S.Container>
    </div>
  );
};

export default InsightsSection;
